import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { 
  Bell, 
  Check, 
  CheckCheck, 
  Trash2, 
  Settings, 
  AlertCircle, 
  Info, 
  AlertTriangle, 
  CheckCircle
} from 'lucide-react';
import type { RootState, AppDispatch } from '@/store';
import { 
  fetchNotifications, 
  fetchNotificationSummary,
  markNotificationAsRead,
  bulkMarkAsRead,
  deleteNotification,
  clearError
} from '@/store/slices/notificationsSlice';
import type { Notification, NotificationType } from '@/types';
import { formatDistanceToNow } from 'date-fns';

const NotificationDropdown: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { notifications, unreadCount, isLoading, error } = useSelector(
    (state: RootState) => state.notifications
  );
  
  const [isOpen, setIsOpen] = useState(false);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchNotifications({ unreadOnly: showUnreadOnly, limit: 20 }));
    }
  }, [dispatch, isOpen, showUnreadOnly]);

  // Fetch unread count periodically
  useEffect(() => {
    // Initial fetch
    dispatch(fetchNotificationSummary());
    
    // Set up interval for periodic updates
    const interval = setInterval(() => {
      dispatch(fetchNotificationSummary());
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, [dispatch]);

  const handleToggleDropdown = () => {
    setIsOpen(!isOpen);
    if (error) {
      dispatch(clearError());
    }
  };

  const handleMarkAsRead = async (notificationId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    await dispatch(markNotificationAsRead(notificationId));
  };

  const handleMarkAllAsRead = async () => {
    const unreadIds = notifications
      .filter(n => !n.isRead)
      .map(n => n.id);
    
    if (unreadIds.length > 0) {
      await dispatch(bulkMarkAsRead(unreadIds));
    }
  };

  const handleDeleteNotification = async (notificationId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    await dispatch(deleteNotification(notificationId));
  };

  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case 'INFO':
        return <Info className="w-4 h-4 text-status-info" />;
      case 'SUCCESS':
        return <CheckCircle className="w-4 h-4 text-status-success" />;
      case 'WARNING':
        return <AlertTriangle className="w-4 h-4 text-status-warning" />;
      case 'ERROR':
        return <AlertCircle className="w-4 h-4 text-status-error" />;
      default:
        return <Info className="w-4 h-4 text-status-info" />;
    }
  };

  const getNotificationBorderColor = (type: NotificationType) => {
    switch (type) {
      case 'INFO':
        return 'border-l-status-info';
      case 'SUCCESS':
        return 'border-l-status-success';
      case 'WARNING':
        return 'border-l-status-warning';
      case 'ERROR':
        return 'border-l-status-error';
      default:
        return 'border-l-status-info';
    }
  };

  const filteredNotifications = showUnreadOnly 
    ? notifications.filter(n => !n.isRead)
    : notifications;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Notification Bell Button */}
      <button
        onClick={handleToggleDropdown}
        className="relative p-2 hover:bg-muted rounded-lg transition-colors"
      >
        <Bell className="w-5 h-5 text-muted-foreground" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-accent-blue text-text-inverse text-xs font-medium rounded-full flex items-center justify-center px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-muted border border-border rounded-lg shadow-xl z-50 max-h-[600px] flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-border">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-foreground">Notifications</h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    className="text-xs text-accent-blue hover:text-accent-cyan flex items-center gap-1"
                    title="Mark all as read"
                  >
                    <CheckCheck className="w-3 h-3" />
                    Mark all read
                  </button>
                )}
                <Link
                  to="/notifications/preferences"
                  className="p-1 hover:bg-muted/50 rounded text-muted-foreground hover:text-foreground"
                  title="Notification preferences"
                  onClick={() => setIsOpen(false)}
                >
                  <Settings className="w-4 h-4" />
                </Link>
              </div>
            </div>
            
            {/* Filter Toggle */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowUnreadOnly(false)}
                className={`px-3 py-1 text-xs rounded-full transition-colors ${
                  !showUnreadOnly 
                    ? 'bg-accent-blue text-foreground' 
                    : 'bg-muted/50 text-foreground hover:bg-bg-hover'
                }`}
              >
                All ({notifications.length})
              </button>
              <button
                onClick={() => setShowUnreadOnly(true)}
                className={`px-3 py-1 text-xs rounded-full transition-colors ${
                  showUnreadOnly 
                    ? 'bg-accent-blue text-foreground' 
                    : 'bg-muted/50 text-foreground hover:bg-bg-hover'
                }`}
              >
                Unread ({unreadCount})
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="p-4 text-center text-muted-foreground">
                <div className="animate-spin w-6 h-6 border-2 border-accent-cyan border-t-transparent rounded-full mx-auto mb-2"></div>
                Loading notifications...
              </div>
            ) : error ? (
              <div className="p-4 text-center text-status-error">
                <AlertCircle className="w-6 h-6 mx-auto mb-2" />
                {error}
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <Bell className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">
                  {showUnreadOnly ? 'No unread notifications' : 'No notifications yet'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {filteredNotifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={handleMarkAsRead}
                    onDelete={handleDeleteNotification}
                    getIcon={getNotificationIcon}
                    getBorderColor={getNotificationBorderColor}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-border">
            <Link
              to="/notifications"
              className="block w-full text-center text-sm text-accent-blue hover:text-accent-cyan transition-colors"
              onClick={() => setIsOpen(false)}
            >
              View all notifications
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string, event: React.MouseEvent) => void;
  onDelete: (id: string, event: React.MouseEvent) => void;
  getIcon: (type: NotificationType) => React.ReactNode;
  getBorderColor: (type: NotificationType) => string;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onDelete,
  getIcon,
  getBorderColor,
}) => {
  return (
    <div
      className={`p-4 hover:bg-bg-hover transition-colors border-l-4 ${getBorderColor(notification.type)} ${
        !notification.isRead ? 'bg-muted/50' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 mt-0.5">
          {getIcon(notification.type)}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4 className={`text-sm font-medium ${
              notification.isRead ? 'text-foreground' : 'text-foreground'
            }`}>
              {notification.title}
            </h4>
            
            {/* Actions */}
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {!notification.isRead && (
                <button
                  onClick={(e) => onMarkAsRead(notification.id, e)}
                  className="p-1 hover:bg-bg-hover rounded text-muted-foreground hover:text-foreground"
                  title="Mark as read"
                >
                  <Check className="w-3 h-3" />
                </button>
              )}
              <button
                onClick={(e) => onDelete(notification.id, e)}
                className="p-1 hover:bg-bg-hover rounded text-muted-foreground hover:text-status-error"
                title="Delete notification"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>

          <p className={`text-xs mt-1 ${
            notification.isRead ? 'text-muted-foreground' : 'text-foreground'
          }`}>
            {notification.message}
          </p>

          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-muted-foreground">
              {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
            </span>
            
            {!notification.isRead && (
              <div className="w-2 h-2 bg-accent-blue rounded-full"></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationDropdown;